import DeleteIcon from "@mui/icons-material/Delete";
import DeselectIcon from "@mui/icons-material/Deselect";
import DownloadIcon from "@mui/icons-material/Download";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { MenuItem, PopoverPosition } from "@mui/material";
import * as React from "react";

import { useAnalytics } from "@/providers";
import { RobotoStyledPopoverMenu } from "@/shared/components";
import {
  FileNode,
  FileSystemNode,
  isDirectory,
  isFile,
  downloadFile,
} from "@/shared/domain/files/filesystem";
import { useDeleteDirectoryMutation } from "@/shared/domain/files/hooks/useDeleteDirectoryMutation";
import { useDeleteFileMutation } from "@/shared/domain/files/hooks/useDeleteFileMutation";
import { LoggerService } from "@/shared/services";

interface ActionMenuProps {
  anchorPos: PopoverPosition | undefined;
  checked: boolean;
  detailsExpandable: boolean;
  row: FileSystemNode;
  rowIsFileType: boolean;
  fetchSignedUrl: (arg: FileNode) => Promise<string | undefined>;
  handleRenameNode: () => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  onClose: () => void;
  onDeleteSuccess: (row: FileSystemNode) => void;
  openContent: () => void;
  openDetails: () => void;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  setAnchorPos: (arg: PopoverPosition | undefined) => void;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorPos,
  checked,
  detailsExpandable,
  row,
  rowIsFileType,
  fetchSignedUrl,
  handleRenameNode,
  handleSelectRows,
  onClose,
  openContent,
  openDetails,
  onDeleteSuccess,
  handleActionConfirmation,
  setAnchorPos,
}) => {
  const deleteFileMutation = useDeleteFileMutation();
  const deleteDirectoryMutation = useDeleteDirectoryMutation();
  const { trackEvent } = useAnalytics();

  const handleClose = () => {
    setAnchorPos(undefined);
    onClose();
  };

  const onClickDownload = async () => {
    if (!isFile(row)) {
      return;
    }

    const signedUrl = await fetchSignedUrl(row);

    if (signedUrl) {
      downloadFile(row.name, signedUrl);
    }
    handleClose();
  };

  const details = detailsExpandable ? (
    <MenuItem
      key="details"
      disableRipple
      onClick={() => {
        handleClose();
        openDetails();
      }}
    >
      <ListOutlinedIcon />
      Details
    </MenuItem>
  ) : null;

  const fileMenuOptions = [
    <MenuItem
      key="view"
      disableRipple
      onClick={() => {
        handleClose();
        openContent();
      }}
    >
      <VisibilityRoundedIcon />
      View
    </MenuItem>,
    details,
    <MenuItem
      key="download"
      disableRipple
      onClick={() => {
        void onClickDownload();
      }}
    >
      <DownloadIcon />
      Download
    </MenuItem>,
  ];

  return (
    <>
      <RobotoStyledPopoverMenu
        id="selection-menu"
        MenuListProps={{
          "aria-labelledby": "selection-button",
        }}
        anchorReference="anchorPosition"
        anchorPosition={anchorPos}
        open={Boolean(anchorPos)}
        onClose={handleClose}
      >
        <MenuItem
          disableRipple
          onClick={() => {
            trackEvent("RenameFileNodeClicked", { name: row.name });
            handleRenameNode();
            handleClose();
          }}
        >
          <DriveFileRenameOutlineIcon />
          Rename
        </MenuItem>
        {checked && (
          <MenuItem
            disableRipple
            onClick={() => {
              handleClose();
              handleSelectRows([row], false);
            }}
          >
            <DeselectIcon />
            Deselect
          </MenuItem>
        )}
        {!checked && (
          <MenuItem
            disableRipple
            onClick={() => {
              handleClose();
              handleSelectRows([row], true);
            }}
          >
            <SelectAllIcon />
            Select
          </MenuItem>
        )}

        {rowIsFileType && fileMenuOptions}

        <MenuItem
          disableRipple
          onClick={() => {
            handleClose();
            handleActionConfirmation(
              `Delete ${isFile(row) ? "File" : "Directory"}`,
              `Are you sure you want to delete ${row.name}?`,
              async function () {
                try {
                  if (isDirectory(row)) {
                    await deleteDirectoryMutation.mutateAsync({
                      datasetId: row.directory.association_id,
                      directoryPaths: [row.directory.relative_path],
                    });
                  } else if (isFile(row)) {
                    await deleteFileMutation.mutateAsync({
                      file: row.file,
                      resourceOwnerId: row.file.org_id,
                    });
                  }

                  onDeleteSuccess(row);
                } catch (e) {
                  LoggerService.error("Failed to delete file", e);
                }
              },
            );
          }}
        >
          <DeleteIcon />
          Delete
        </MenuItem>
      </RobotoStyledPopoverMenu>
    </>
  );
};
